'use client'

import { useState } from 'react'
import { useRouter } from 'next/navigation'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { ArrowRight } from 'lucide-react'
import { Dropzone } from '@/components/patterns/dropzone'
import { Button } from '@/components/ui/button'
import { Field, FieldLabel, FieldDescription, FieldError } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { OnboardingLayout } from '@/features/onboarding/components/onboarding-layout'
import {
  uploadDocumentSchema,
  type UploadDocumentFormValues,
} from '@/features/onboarding/schemas/upload-cas.schema'
import { useOnboardingStore } from '@/features/onboarding/store/onboarding-store'

const KIND_OPTIONS = [
  { value: 'cas_pdf', label: 'CAS Statement (CDSL / NSDL / CAMS / KFintech)' },
  { value: 'broker_xlsx', label: 'Portfolio / Trade file (Excel)' },
] as const

export function UploadDocumentView() {
  const router = useRouter()
  const setDocument = useOnboardingStore((s) => s.setDocument)
  const [file, setFile] = useState<File | null>(null)

  const form = useForm<UploadDocumentFormValues>({
    resolver: zodResolver(uploadDocumentSchema),
    mode: 'onTouched',
    defaultValues: { kind: 'cas_pdf', password: '' },
  })
  const kind = form.watch('kind')

  const onSubmit = form.handleSubmit((values) => {
    if (!file) return
    setDocument({ file, kind: values.kind, password: values.password || undefined })
    router.push('/submission-successful')
  })

  return (
    <OnboardingLayout
      title="Upload Your Statement"
      description="A few quick steps to bring your holdings into Nivex securely."
      step={2}
      totalSteps={4}
      fitContent
    >
      <div className="flex flex-col gap-3">
        <Field>
          <FieldLabel htmlFor="doc-kind">Document type</FieldLabel>
          <Select
            value={kind}
            onValueChange={(v) => form.setValue('kind', v as UploadDocumentFormValues['kind'])}
          >
            <SelectTrigger id="doc-kind" className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {KIND_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>

        <Dropzone
          accept={kind === 'cas_pdf' ? '.pdf' : '.xlsx,.xls'}
          helpText={kind === 'cas_pdf' ? '.pdf up to 10 MB' : '.xlsx/.xls up to 10 MB'}
          file={file}
          onFileChange={setFile}
        />

        {kind === 'cas_pdf' && (
          <Field>
            <FieldLabel htmlFor="doc-password">Statement password</FieldLabel>
            <Input
              id="doc-password"
              type="text"
              placeholder="PAN, or your KFintech password"
              aria-invalid={!!form.formState.errors.password}
              {...form.register('password')}
            />
            <FieldError errors={[form.formState.errors.password]} />
            <FieldDescription className="text-xs leading-snug">
              PAN for CAMS/NSDL/CDSL, or your KFintech password.
            </FieldDescription>
          </Field>
        )}

        <Button className="w-full" disabled={!file} onClick={onSubmit}>
          Continue
          <ArrowRight className="size-4" />
        </Button>
      </div>
    </OnboardingLayout>
  )
}
